<template>
  <Flex
    direction="column"
    :gap="1"
    :class="[$style.base, { [$style.smallScreen]: $screen === 's' }]"
  >
    <BaseCard
      :heading="title"
      v-test="'website-service-title-card'"
    >
      <Flex
        direction="column"
        :gap="1"
      >
        <Flex
          :gap="0.5"
          direction="column"
        >
          <BaseText>{{ description }}</BaseText>
        </Flex>
        <Flex
          :gap="1"
          wrap
        >
          <BaseButton
            v-if="isLive"
            :href="changeRequestUrl"
            :disabled="isCancellationRequested"
            v-test="'website-service-request-changes-button'"
          >
            {{ $t('website_service.dashboard.request_changes') }}
          </BaseButton>
          <HelpPageButton v-else-if="isReactivationRequested" />
          <BaseButton
            v-else
            @click="reactivateWebsite"
            v-test="'website-service-reactivate-button'"
          >
            {{ $t('website_service.dashboard.reactivate') }}
          </BaseButton>
          <BaseButton
            v-if="isLive"
            color="inverted"
            icon="external_link"
            :href="urlWithProtocol"
            v-test="'website-view-button'"
          >
            {{ $t('website_service.dashboard.view_website') }}
          </BaseButton>
        </Flex>
      </Flex>
    </BaseCard>

    <BaseCard
      v-if="isLive"
      :heading="$t('website_service.dashboard.share_website')"
      v-test="'website-service-share-website-card'"
    >
      <Flex
        direction="column"
        :gap="1"
      >
        <BaseText>
          {{ $t('website_service.dashboard.share_website_description') }}
        </BaseText>
        <BaseCodeField
          :code="website?.url || ''"
          buttonColor="inverted"
          v-test="'website-copy-url-button'"
        />
      </Flex>
    </BaseCard>

    <GiftcardWidgetSettings v-if="showGiftcardWidgetSettings" />

    <BaseCard
      :heading="$t('website_service.dashboard.subscription')"
      v-test="'website-service-subscription-card'"
    >
      <Flex
        direction="column"
        :gap="1"
      >
        <Flex :gap="0.5">
          <BaseLabel
            :color="isLive ? 'success' : isReactivationRequested ? 'warning' : 'error'"
          >
            {{ $t(`website_service.dashboard.${isLive ? 'active' : isReactivationRequested ? 'pending_activation' : 'inactive'}`) }}
          </BaseLabel>
        </Flex>
        <Flex
          direction="column"
          :gap="0.5"
        >
          <BaseHeading size="s">
            {{ $t('website_service.dashboard.plan') }}
          </BaseHeading>
          <BaseText>
            {{ $t('website_service.dashboard.plan_description', { price, ...brandName() }) }}
          </BaseText>
        </Flex>
        <Flex
          direction="column"
          :gap="0.5"
        >
          <BaseHeading size="s">
            {{ $t('website_service.dashboard.domain') }}
          </BaseHeading>
          <BaseText>{{ website?.url }}</BaseText>
        </Flex>
        <BaseText
          v-if="isLive && !isCancellationRequested"
          @click="showRequestCancellationModal = true"
          v-test="'website-service-cancel-subscription-button'"
        >
          {{ $t('website_service.dashboard.cancel_subscription') }}
        </BaseText>
        <BaseAlert
          v-if="isLive && isCancellationRequested"
          size="s"
          color="warning"
          :text="$t('website_service.dashboard.cancellation_requested')"
          v-test="'website-service-cancellation-requested-alert'"
        />
      </Flex>
    </BaseCard>

    <CancellationModal
      v-if="showRequestCancellationModal"
      @close="showRequestCancellationModal = false"
    />
    <ReactivationModal
      v-if="showRequestReactivationModal"
      @close="showRequestReactivationModal = false"
    />
  </Flex>
</template>

<script
  setup
  lang="ts"
>
import { computed, inject, type Ref, ref } from 'vue';
import { useI18n } from 'vue-i18n';
import { brandName } from '@/helpers/brand-name';
import HelpPageButton from '@/modules/online/website-service/HelpPageButton.vue';
import { type Website, WebsiteStatusEnum } from '@/types';
import unleash from '@/unleash';
import { Airtable } from '../Airtable';
import CancellationModal from './CancellationModal.vue';
import GiftcardWidgetSettings from './GiftcardWidgetSettings.vue';
import ReactivationModal from './ReactivationModal.vue';

const { t } = useI18n();

const website = inject<Ref<Website>>('website');
const price = inject<Ref<string>>('price');
const showRequestCancellationModal = ref(false);
const showRequestReactivationModal = ref(false);

const urlWithProtocol = computed(() => {
  const url = website.value?.url;
  if (!url) return '';

  if (url.startsWith('http')) {
    return url;
  }

  return `https://${url}`;
});

const title = computed(() => {
  if (isLive.value) {
    return t('website_service.dashboard.title');
  } else if (isReactivationRequested.value) {
    return t('website_service.dashboard.title_reactivation');
  } else {
    return t('website_service.dashboard.title_inactive');
  }
});

const description = computed(() => {
  if (isLive.value) {
    return t('website_service.dashboard.description');
  } else if (isReactivationRequested.value) {
    return t('website_service.dashboard.description_reactivation');
  } else {
    return t('website_service.dashboard.description_inactive');
  }
});

const changeRequestUrl = computed(() => {
  const { changeRequestUrl } = Airtable();
  return changeRequestUrl;
});

const isLive = computed(() => website.value?.status === WebsiteStatusEnum.Live);
const showGiftcardWidgetSettings = computed(
  () =>
    (unleash.isEnabled('WebsiteServiceGiftcardWidget') &&
      website.value?.status === WebsiteStatusEnum.Live) ||
    website.value?.status === WebsiteStatusEnum.Review,
);
const isCancellationRequested = computed(
  () => !!website.value?.cancellationRequestedAt,
);
const isReactivationRequested = computed(
  () => !!website.value?.reactivationRequestedAt,
);

const reactivateWebsite = () => {
  showRequestReactivationModal.value = true;
};
</script>

<style
  lang="scss"
  module
>
.image {
  width: calc(1/6 * 100%);
  min-width: 160px;
}
</style>
